import React, { useState, forwardRef } from 'react';

/**
 * PasswordInput
 * --------------
 * Drop-in replacement for `<input type="password" ... />` that adds a
 * trailing eye / eye-off button to toggle visibility.
 *
 * Accepts ALL standard <input> props (value, onChange, name, required,
 * minLength, placeholder, autoComplete, disabled, id, ...). The button
 * is positioned absolutely inside a relative wrapper.
 *
 * Wrapper styling:
 *   - `wrapperClassName` -> applied to the outer <div>. Defaults to "relative".
 *   - `className`        -> forwarded to the inner <input> exactly as-is so
 *                           the input keeps the same Tailwind / form classes
 *                           it had before. We just append `pr-10` if the
 *                           caller didn't already include their own right
 *                           padding so the eye doesn't overlap the text.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = '', wrapperClassName = 'relative', toggleClassName = '', ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  // Only auto-add right padding if the caller didn't already set one.
  const hasRightPad = /(^|\s)pr-\d/.test(className);
  const inputClass = hasRightPad ? className : `${className} pr-10`.trim();
  return (
    <div className={wrapperClassName} style={{ position: wrapperClassName.includes('relative') ? undefined : 'relative' }}>
      <input
        {...rest}
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={inputClass}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        tabIndex={-1}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        className={`absolute top-1/2 -translate-y-1/2 right-2 p-1 text-gray-500 hover:text-gray-800 focus:outline-none ${toggleClassName}`}
        style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
      >
        {visible ? (
          // eye-off
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.77 19.77 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.86 19.86 0 0 1-3.17 4.19"/>
            <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        ) : (
          // eye
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </button>
    </div>
  );
});

export default PasswordInput;
